import { Alert, Form, FormInstance, Input, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n/i18n';
import './UserForm.scss';
import { UserAccountStatus, UserRole } from '../../gen/proto/api/gateway/v1/user_pb';
import useAuthStore from '../../stores/auth.store';
import { userRoleLabel } from '../../utils/mapTranslationEnums';
import useTenantStore, { ALL_TENANTS_VALUE } from '../../stores/tenant.store';
import { userApi } from '../../api/user.api';

interface UserFormProps {
  name?: string;
  email?: string;
  role?: UserRole;
  state?: UserAccountStatus;
  tenantUuid?: string;
  form?: FormInstance;
  onFinish?: () => void;
}

const checkIfAdminInLocation = async (tenantUuid: string): Promise<boolean> => {
  try {
    const response = await userApi.getAdminByTenant(tenantUuid);
    return response.length > 0;
  } catch {
    return true;
  }
};

const UserForm = ({ name, email, role, state, tenantUuid, form, onFinish }: UserFormProps) => {
  const { t } = useTranslation('users', { keyPrefix: 'modal' });
  const isAll = useTenantStore((state) => state.selectedTenant === ALL_TENANTS_VALUE);
  const tenants = useTenantStore((state) => state.tenants);

  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);
  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);

  const ROLES = [
    ...(isSuperAdmin ? [{ value: UserRole.COMPANY_ADMIN, label: userRoleLabel(UserRole.COMPANY_ADMIN) }] : []),
    { value: UserRole.ADMIN, label: userRoleLabel(UserRole.ADMIN) },
    { value: UserRole.USER, label: userRoleLabel(UserRole.USER) },
  ];

  const selectedRole = Form.useWatch('role', form);
  const selectedTenant = Form.useWatch('tenant', form);
  const [tenantHasAdmin, setTenantHasAdmin] = useState(false);

  useEffect(() => {
    if (selectedRole !== UserRole.COMPANY_ADMIN || !selectedTenant) {
      setTenantHasAdmin(false);
      return;
    }
    let cancelled = false;
    checkIfAdminInLocation(selectedTenant).then((hasAdmin) => {
      if (!cancelled) setTenantHasAdmin(hasAdmin);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedRole, selectedTenant]);

  return (
    <Form
      form={form}
      layout="vertical"
      className={'Modal__formFile'}
      initialValues={{ name, email, role, state, tenant: tenantUuid }}
      onFinish={onFinish}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.defaultPrevented) {
          e.preventDefault();
          form?.submit();
        }
      }}
    >
      <Form.Item
        name="name"
        label={t('name')}
        rules={[{ required: true }, { pattern: /^\S+(\s+\S+)+$/, message: t('errorName') }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="email"
        label={t('mail')}
        rules={[{ required: true }, { type: 'email', message: t('errorEmail') }]}
      >
        <Input />
      </Form.Item>
      {isSuperAdminOrCompanyAdmin && (
        <>
          <div className={'Modal__accountData'}>
            <Form.Item
              className={'Modal__accountData__selector'}
              name="role"
              label={t('role')}
              rules={[{ required: true }]}
            >
              <Select options={ROLES} />
            </Form.Item>
          </div>
          {isAll && (
            <Form.Item name="tenant" label={t('tenant')} rules={[{ required: true }]}>
              <Select options={tenants} />
            </Form.Item>
          )}
          {tenantHasAdmin && (
            <Alert type="error" showIcon title={t('tenantHasAdmin')} className={'Modal__tenantHasAdmin'} />
          )}
        </>
      )}
    </Form>
  );
};

export default UserForm;
